/**
 * 设备（资源）管理模块（OFBiz 新接口）
 * ------------------------------------------------------------------
 * 对应后端：POST /itoss/control/json/equipment/*
 *
 * 迁移自前端原接口（odata_equipment_client.js / odataEquipment.js / src/api/index.js）：
 *   getEquipment, setEquipment, updateEquipment, getAllEquipments,
 *   getEquipmentAllData, moveEquipments
 *
 * 字段映射（旧 PascalCase -> 新 camelCase）：
 *   RecId            -> equipmentId
 *   Title            -> fixedAssetName
 *   ServerAddress    -> IP_ADDRESS（存 FixedAssetAttribute）
 *   NodeType         -> 固定 EQUIPMENT
 *   ParentGroupId    -> parentFixedAssetId
 *   (序列号)         -> serialNumber
 *   (类别)           -> classEnumId
 *   (位置)           -> locatedAtFacilityId
 */
import { ofbiz } from './http';

/**
 * 设备列表（分页，替代旧 getEquipmentAllData）
 * @param {object} [query] { parentFixedAssetId, classEnumId, equipStatus,
 *                          fixedAssetName, serialNumber, keyword }
 * @param {number} [page=1]
 * @param {number} [pageSize=20]
 * @returns {Promise<{list, total, page, pageSize}>}
 */
export function getEquipmentList(query = {}, page = 1, pageSize = 20) {
  return ofbiz.postPage('equipment/list', query, page, pageSize);
}

/**
 * 设备详情（替代旧 getEquipment）
 * @param {string} equipmentId
 * @returns {Promise<object>}
 */
export function getEquipmentById(equipmentId) {
  return ofbiz.post('equipment/getById', null, { equipmentId });
}

/**
 * 新增设备（替代旧 setEquipment / createEquipment）
 * @param {object} data 见 OpenAPI：fixedAssetName(必填), classEnumId,
 *                      parentFixedAssetId, locatedAtFacilityId, serialNumber,
 *                      partyId, purchaseCost, assetTag, IP_ADDRESS, MAC_ADDRESS,
 *                      OS_TYPE, VENDOR, MODEL, SSH_*, SNMP_*, CONTACT, DESCRIPTION,
 *                      EQUIP_STATUS
 * @returns {Promise<object>}
 */
export function createEquipment(data) {
  return ofbiz.post('equipment/create', data);
}

/**
 * 更新设备（替代旧 updateEquipment）
 * @param {object} data 必含 equipmentId
 * @returns {Promise<object>}
 */
export function updateEquipment(data) {
  return ofbiz.post('equipment/update', data);
}

/**
 * 删除设备（替代旧 deleteEquipment）
 * @param {string} equipmentId
 * @returns {Promise<object>}
 */
export function deleteEquipment(equipmentId) {
  return ofbiz.post('equipment/delete', null, { equipmentId });
}

/**
 * 设备下拉选项（轻量，替代旧 getAllEquipments）
 * @param {object} [opts]
 * @param {string} [opts.parentFixedAssetId] 所属分组 ID
 * @param {string} [opts.classEnumId]        设备类型
 * @returns {Promise<Array>} [{ equipmentId, fixedAssetName, ipAddress, ... }]
 */
export function getEquipmentOptions({ parentFixedAssetId, classEnumId } = {}) {
  return ofbiz.post('equipment/options', null, { parentFixedAssetId, classEnumId });
}

/**
 * 设备类型清单（树形，替代旧前端静态 resourceData / EquipmentTypeRel）
 * ------------------------------------------------------------------
 * 返回设备类型树（大类 → 具体类型），创建设备时选择设备类型用。
 * 新接口对应：POST /itoss/control/json/equipment/types
 *
 * @param {object} [opts]
 * @param {string} [opts.category] 指定大类 enumTypeId（如 IT_EQT_SRV），不传返回全部
 * @returns {Promise<{list:Array, total:number}>}
 *    list 元素：{ categoryCode, categoryName, types: [{ typeCode, typeName, typeId }] }
 *    typeCode 即设备的 EQUIP_TYPE 值（如 Equipment.Windows），创建设备时传入。
 */
export function getEquipmentTypes({ category } = {}) {
  return ofbiz.post('equipment/types', null, { category });
}
